// src/context/AuthContext.js
import { createContext, useContext, useEffect, useState, useRef } from "react";
import { api, mapRow } from "../utils/api";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

function parseTokenUser() {
  const token = localStorage.getItem("portfolio_token");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return { uid: payload.uid, email: payload.email, role: payload.role };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [adminProfile, setAdminProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionCheckRef = useRef(null);

  async function loadSessionUser() {
    const tokenUser = parseTokenUser();
    if (!tokenUser?.uid) {
      setCurrentUser(null);
      setUserRole(null);
      setAdminProfile(null);
      return false;
    }

    try {
      const profile = await api.get(`/faculty/${tokenUser.uid}/profile`);
      if (!profile) {
        localStorage.removeItem("portfolio_token");
        setCurrentUser(null);
        setUserRole(null);
        setAdminProfile(null);
        return false;
      }

      const data = mapRow(profile);

      if (data.status === "inactive") {
        localStorage.removeItem("portfolio_token");
        sessionStorage.removeItem("sessionId");
        setCurrentUser(null);
        setUserRole(null);
        setAdminProfile(null);
        return false;
      }

      const localSessionId = sessionStorage.getItem("sessionId");
      const serverSessionId = data?.sessionId;
      if (serverSessionId && localSessionId && localSessionId !== serverSessionId) {
        const localNum = Number(localSessionId);
        const firestoreNum = Number(serverSessionId);
        if (!Number.isNaN(localNum) && !Number.isNaN(firestoreNum)) {
          if (firestoreNum > localNum) {
            localStorage.removeItem("portfolio_token");
            sessionStorage.removeItem("sessionId");
            setCurrentUser(null);
            setUserRole(null);
            setAdminProfile(null);
            alert("This session has been terminated because you logged in from another device.");
            return false;
          }
          await api.put(`/faculty/${tokenUser.uid}/profile`, { sessionId: localSessionId });
        } else {
          localStorage.removeItem("portfolio_token");
          sessionStorage.removeItem("sessionId");
          setCurrentUser(null);
          setUserRole(null);
          setAdminProfile(null);
          alert("This session has been terminated because you logged in from another device.");
          return false;
        }
      } else if (serverSessionId && !localSessionId) {
        sessionStorage.setItem("sessionId", serverSessionId);
      }

      setUserRole(data?.role || tokenUser.role || "faculty");
      setAdminProfile(data || null);
      setCurrentUser({ uid: tokenUser.uid, email: data.email || tokenUser.email, ...tokenUser });
      return true;
    } catch (err) {
      console.error("Session load error:", err);
      localStorage.removeItem("portfolio_token");
      setCurrentUser(null);
      setUserRole(null);
      setAdminProfile(null);
      return false;
    }
  }

  async function adminLogin(email, password) {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Login failed");
    if (data.user.role !== "admin" && data.user.role !== "superadmin") {
      throw new Error("Not an admin account");
    }
    localStorage.setItem("portfolio_token", data.token);
    localStorage.setItem("portfolio_user", JSON.stringify(data.user));
    setCurrentUser(data.user);
    setUserRole(data.user.role);
    const profile = await api.get(`/faculty/${data.user.uid}/profile`).catch(() => null);
    if (profile) setAdminProfile(mapRow(profile));
    return data;
  }

  async function facultyLogin(email, password) {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Invalid credentials");

    localStorage.setItem("portfolio_token", data.token);

    const profileRes = await api.get(`/faculty/${data.user.uid}/profile`);
    const profile = mapRow(profileRes);
    if (!profile) {
      localStorage.removeItem("portfolio_token");
      throw new Error("Account not found. Please contact the administrator.");
    }
    if (profile.status === "inactive") {
      localStorage.removeItem("portfolio_token");
      throw new Error("Your account has been deactivated. Please contact admin.");
    }

    const sessionId = Date.now().toString();
    sessionStorage.setItem("sessionId", sessionId);
    const lastLogin = new Date().toISOString();
    await api.put(`/faculty/${data.user.uid}/profile`, { sessionId, lastLogin });

    setCurrentUser({ uid: data.user.uid, email: data.user.email });
    setUserRole(profile.role || data.user.role || "faculty");
    setAdminProfile(profile);

    return { user: { uid: data.user.uid, ...data.user } };
  }

  async function createFacultyAccount(email, password, profileData) {
    const body = {
      ...profileData,
      email,
      password,
      role: profileData?.role || "faculty",
      mustResetPassword: true,
      createdAt: new Date().toISOString(),
    };
    const res = await api.post("/users", body);
    return { user: { uid: res.id, email } };
  }

  async function createBulkFacultyAccounts(rows, onProgress) {
    let success = 0;
    let fail = 0;
    for (let i = 0; i < rows.length; i++) {
      const { email, password, profileData } = rows[i];
      if (onProgress) onProgress(i + 1, rows.length);
      try {
        await createFacultyAccount(email, password, {
          ...profileData,
          password,
        });
        success++;
      } catch (e) {
        console.error("Failed to import user:", email, e);
        fail++;
      }
    }
    return { success, fail };
  }

  async function logout() {
    localStorage.removeItem("portfolio_token");
    localStorage.removeItem("portfolio_user");
    sessionStorage.removeItem("sessionId");
    setCurrentUser(null);
    setUserRole(null);
    setAdminProfile(null);
  }

  useEffect(() => {
    (async () => {
      await loadSessionUser();
      setLoading(false);
    })();

    sessionCheckRef.current = setInterval(() => {
      if (localStorage.getItem("portfolio_token")) {
        loadSessionUser();
      }
    }, 30000);

    return () => {
      if (sessionCheckRef.current) clearInterval(sessionCheckRef.current);
    };
  }, []);

  const value = {
    currentUser,
    userRole,
    adminProfile,
    adminLogin,
    facultyLogin,
    createFacultyAccount,
    createBulkFacultyAccounts,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
